/**
 * Utilities for formatting phone numbers and building SMS URIs.
 */

/**
 * Formats a raw phone string into standard US/NANP display format: (XXX) XXX-XXXX.
 * If input cannot be parsed as a 10-digit number (or 11-digit starting with 1),
 * returns the trimmed original string.
 *
 * @param {string|number|null|undefined} raw
 * @returns {string}
 */
export function formatPhone(raw) {
  if (raw == null) return '';
  const str = String(raw).trim();
  if (!str) return '';

  const digits = str.replace(/\D/g, '');

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    const nat = digits.slice(1);
    return `(${nat.slice(0, 3)}) ${nat.slice(3, 6)}-${nat.slice(6)}`;
  }

  return str;
}

/**
 * Normalizes a phone number to E.164 format for telecom links (+1XXXXXXXXXX for 10-digit US).
 *
 * @param {string|number|null|undefined} raw
 * @returns {string}
 */
export function toE164(raw) {
  if (raw == null) return '';
  const str = String(raw).trim();
  if (!str) return '';

  const digits = str.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (str.startsWith('+')) {
    return `+${digits}`;
  }
  return digits;
}

/**
 * Generates an RFC 5724 compliant SMS URI (`sms:<recipient>?body=<encoded_text>`).
 *
 * @param {Object} options
 * @param {string|number|null|undefined} options.phone - The target phone number.
 * @param {string} [options.body] - Optional pre-filled SMS message text.
 * @returns {string} The formatted sms: URI, or empty string if phone is missing.
 */
export function buildSmsUri({ phone, body } = {}) {
  const normalized = toE164(phone);
  if (!normalized) return '';

  const recipient = encodeURIComponent(normalized);
  if (body && typeof body === 'string' && body.trim()) {
    return `sms:${recipient}?body=${encodeURIComponent(body.trim())}`;
  }

  return `sms:${recipient}`;
}

const phoneUtils = {
  formatPhone,
  toE164,
  buildSmsUri,
};

export default phoneUtils;
